/**
 * ExternalApiDock — small floating widget showing total active external
 * API calls + alerts when any service is paused / rate-limited. Visible
 * to admin/sub_admin/business_head on every page (auto-hidden on the
 * /admin?tab=external-apis page itself).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import {
  Activity, Loader2, Pause, AlertTriangle, ChevronDown, ChevronUp,
  ChevronRight, Server,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ADMIN_ROLES = ['admin', 'sub_admin', 'business_head'];
const POLL_MS = 5000;

export default function ExternalApiDock() {
  const { currentUser } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [collapsed, setCollapsed] = useState(true);
  const [hidden, setHidden] = useState(false);

  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);
  const requesterId = currentUser?.id;

  const fetchSvcs = useCallback(async () => {
    if (!requesterId) return;
    try {
      const res = await axios.get(`${API}/admin/external-apis`, {
        params: { requester_id: requesterId },
      });
      setServices(res.data.services || []);
    } catch (_) { /* silent */ }
  }, [requesterId]);

  useEffect(() => {
    if (!isAdmin || !requesterId) return;
    fetchSvcs();
    let t = null;
    const start = () => { if (!t) t = setInterval(fetchSvcs, POLL_MS); };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    if (document.visibilityState === 'visible') start();
    const onVis = () => {
      if (document.visibilityState === 'visible') { fetchSvcs(); start(); } else stop();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAdmin, requesterId, fetchSvcs]);

  const onTab = location.pathname === '/admin'
    && ((location.hash || '').replace('#', '') === 'external-apis'
        || new URLSearchParams(location.search).get('tab') === 'external-apis');

  if (!isAdmin || !requesterId) return null;
  if (onTab || hidden) return null;
  if (services.length === 0) return null;

  const totalActive = services.reduce((a, s) => a + (s.active || 0), 0);
  const paused = services.filter(s => s.paused);
  const rateLimited = services.filter(s => s.rl_per_min > 0);
  const erroring = services.filter(s => s.err_per_min > 0 && s.rl_per_min === 0);
  const showDock = totalActive > 0 || paused.length > 0 || rateLimited.length > 0 || erroring.length > 0;
  if (!showDock) return null;

  const accent = paused.length > 0 ? 'border-amber-400 bg-amber-50'
    : rateLimited.length > 0 ? 'border-red-300 bg-red-50'
    : erroring.length > 0 ? 'border-orange-300 bg-orange-50'
    : 'border-purple-300 bg-purple-50';

  return (
    <div
      data-testid="extapi-dock"
      className={`fixed bottom-6 left-6 z-[59] w-[300px] max-w-[calc(100vw-2rem)] rounded-xl shadow-xl overflow-hidden border-2 ${accent}`}
      style={{ boxShadow: '0 10px 40px -10px rgba(124, 58, 237, 0.3)' }}
    >
      <button
        onClick={() => setCollapsed(c => !c)}
        data-testid="extapi-dock-toggle"
        className="w-full flex items-center justify-between px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {totalActive > 0
            ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            : <Activity className="w-3.5 h-3.5 shrink-0" />}
          <span className="font-semibold text-xs truncate">
            {totalActive} external call{totalActive === 1 ? '' : 's'} in flight
          </span>
          {paused.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 font-bold shrink-0">
              {paused.length} paused
            </span>
          )}
          {rateLimited.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-200 text-red-900 font-bold shrink-0">
              429
            </span>
          )}
        </div>
        {collapsed ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {!collapsed && (
        <div className="bg-white/90 divide-y divide-slate-100" data-testid="extapi-dock-list">
          {services.map(s => (
            <div key={s.name} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                {s.paused
                  ? <Pause className="w-3 h-3 text-amber-600 shrink-0" />
                  : s.rl_per_min > 0
                    ? <AlertTriangle className="w-3 h-3 text-red-600 shrink-0" />
                    : s.active > 0
                      ? <Loader2 className="w-3 h-3 animate-spin text-emerald-600 shrink-0" />
                      : <Server className="w-3 h-3 text-slate-300 shrink-0" />}
                <span className="text-[11px] text-slate-700 truncate">{s.label}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] shrink-0">
                <span className="font-mono text-emerald-700 font-semibold">{s.active}</span>
                <span className="text-slate-400">/{s.cap}</span>
                {s.calls_per_min > 0 && (
                  <span className="text-slate-500">· {s.calls_per_min}/min</span>
                )}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
            <button
              onClick={() => navigate('/admin#external-apis')}
              data-testid="extapi-dock-open"
              className="text-[10px] text-purple-700 hover:text-purple-900 font-semibold flex items-center gap-1"
            >
              Open controls <ChevronRight className="w-3 h-3" />
            </button>
            <button
              onClick={() => setHidden(true)}
              data-testid="extapi-dock-hide"
              className="text-[10px] text-slate-400 hover:text-slate-600"
              title="Hide for this session"
            >
              hide
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
